import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PaymentMethodSetupDialog } from '@/components/customer/billing/PaymentMethodSetupDialog';
import { useCustomerPaymentMethod, useRemoveCustomerPaymentMethod } from '@/hooks/customer/useCustomerPaymentMethod';
import { extractJsonApiErrorCode, extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';

function formatBrand(value: string | undefined) {
  if (!value) return '';
  return value.slice(0, 1).toUpperCase() + value.slice(1);
}

function formatExpiry(month?: number, year?: number) {
  if (!month || !year) return '—';
  const paddedMonth = String(month).padStart(2, '0');
  const yearSuffix = String(year).slice(-2);
  return `${paddedMonth}/${yearSuffix}`;
}

export default function CustomerPaymentMethodTab() {
  const { t } = useTranslation();
  const paymentMethodQuery = useCustomerPaymentMethod(true);
  const removeMutation = useRemoveCustomerPaymentMethod();

  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [setupDialogMode, setSetupDialogMode] = useState<'add' | 'update'>('add');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const method = paymentMethodQuery.data ?? null;

  const hasCard = method?.status === 'default' && Boolean(method.last4);
  const canRemove = Boolean(hasCard) && method?.can_remove !== false;
  const blockedReason = method?.blocked_reason?.trim() ? method.blocked_reason : null;

  const summary = useMemo(() => {
    if (!hasCard) return null;
    const brand = formatBrand(method?.brand);
    const last4 = method?.last4 ?? '';
    const expiry = formatExpiry(method?.exp_month, method?.exp_year);
    const label = brand && last4 ? `${brand} •••• ${last4}` : t('customer.billing.paymentMethod.summary.unknown');
    return { label, expiry };
  }, [hasCard, method?.brand, method?.exp_month, method?.exp_year, method?.last4, t]);

  if (paymentMethodQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('customer.billing.tabs.paymentMethod')}</CardTitle>
          <CardDescription>{t('customer.billing.paymentMethod.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-12 w-full" />
          <div className="flex justify-end gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-28" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (paymentMethodQuery.isError) {
    const status = (paymentMethodQuery.error as { status?: number })?.status;
    const isAuthError = status === 401 || status === 403;
    const title = isAuthError
      ? t('customer.billing.paymentMethod.authErrorTitle')
      : t('customer.billing.paymentMethod.loadErrorTitle');
    const description = isAuthError
      ? t('customer.billing.paymentMethod.authErrorDescription')
      : extractJsonApiErrorMessage(paymentMethodQuery.error, t('customer.billing.paymentMethod.loadErrorDescription'));

    return (
      <Alert variant="destructive">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p>{description}</p>
          <Button type="button" variant="secondary" onClick={() => paymentMethodQuery.refetch()}>
            {t('customer.billing.paymentMethod.retry')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const showStripeError = method?.status === 'error' && Boolean(method.message?.trim());

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('customer.billing.tabs.paymentMethod')}</CardTitle>
          <CardDescription>{t('customer.billing.paymentMethod.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showStripeError ? (
            <Alert variant="destructive">
              <AlertTitle>{t('customer.billing.paymentMethod.stripeErrorTitle')}</AlertTitle>
              <AlertDescription>{method?.message}</AlertDescription>
            </Alert>
          ) : null}

          {hasCard && summary ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t('customer.billing.paymentMethod.summary.current')}</p>
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{summary.label}</span>
                  <span className="text-muted-foreground">{t('customer.billing.paymentMethod.summary.exp', { exp: summary.expiry })}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  onClick={() => {
                    setSetupDialogMode('update');
                    setSetupDialogOpen(true);
                  }}
                  disabled={method?.can_update === false}
                >
                  {t('customer.billing.paymentMethod.actions.update')}
                </Button>

                {canRemove ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setRemoveDialogOpen(true)}
                    disabled={removeMutation.isPending}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('customer.billing.paymentMethod.actions.remove')}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <Alert>
              <AlertTitle>{t('customer.billing.paymentMethod.emptyTitle')}</AlertTitle>
              <AlertDescription className="mt-2 space-y-3">
                <p>{t('customer.billing.paymentMethod.emptyDescription')}</p>
                <Button
                  type="button"
                  onClick={() => {
                    setSetupDialogMode('add');
                    setSetupDialogOpen(true);
                  }}
                  disabled={method?.can_update === false}
                >
                  {t('customer.billing.paymentMethod.actions.add')}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {hasCard && method?.can_update === false ? (
            <p className="text-sm text-muted-foreground">{t('customer.billing.paymentMethod.updateDisabled')}</p>
          ) : null}

          {hasCard && !canRemove ? (
            <Alert>
              <AlertTitle>{t('customer.billing.paymentMethod.removeBlockedTitle')}</AlertTitle>
              <AlertDescription>
                {blockedReason ?? t('customer.billing.paymentMethod.removeBlockedDescription')}
              </AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {setupDialogOpen ? (
        <PaymentMethodSetupDialog
          open={setupDialogOpen}
          onOpenChange={setSetupDialogOpen}
          mode={setupDialogMode}
        />
      ) : null}

      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('customer.billing.paymentMethod.removeDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('customer.billing.paymentMethod.removeDialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMutation.isPending}>
              {t('customer.billing.paymentMethod.removeDialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                variant="destructive"
                disabled={removeMutation.isPending}
                onClick={async () => {
                  try {
                    await removeMutation.mutateAsync();
                    toast.success(t('customer.billing.paymentMethod.feedback.removed'));
                    setRemoveDialogOpen(false);
                  } catch (error) {
                    const code = extractJsonApiErrorCode(error);
                    if (code === 'payment_method_removal_blocked') {
                      toast.error(
                        extractJsonApiErrorMessage(
                          error,
                          t('customer.billing.paymentMethod.feedback.removeBlocked'),
                        ),
                      );
                      await paymentMethodQuery.refetch();
                      setRemoveDialogOpen(false);
                      return;
                    }
                    if (code === 'stripe_not_configured') {
                      toast.error(t('customer.billing.paymentMethod.stripeNotConfigured'));
                      setRemoveDialogOpen(false);
                      return;
                    }
                    toast.error(
                      extractJsonApiErrorMessage(error, t('customer.billing.paymentMethod.feedback.removeFailed')),
                    );
                  }
                }}
              >
                {removeMutation.isPending
                  ? t('customer.billing.paymentMethod.actions.removing')
                  : t('customer.billing.paymentMethod.actions.remove')}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
